import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Custom TranslateLoader that supports translation template-specific language files.
 * 
 * Loading strategy:
 * 1. First tries to load: ./assets/i18n/{translationTemplate}/{lang}.json
 * 2. If not found, falls back to: ./assets/i18n/{lang}.json
 * 
 * File structure example:
 * - assets/i18n/en.json (default English)
 * - assets/i18n/ja.json (default Japanese)
 * - assets/i18n/{translationTemplate}/en.json (template-specific English)
 * - assets/i18n/{translationTemplate}/ja.json (template-specific Japanese)
 */
export class TemplateTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json'
  ) { }

  /**
   * Gets the translation template from environment
   */
  private getTranslationTemplate(): string | null {
    try {
      const translationTemplate = localStorage.getItem('template');
      return translationTemplate ? JSON.parse(translationTemplate) : environment.translationTemplate;
    } catch {
      return environment.translationTemplate || null;
    }
  }

  /**
   * Loads translation file with template-specific fallback
   */
  getTranslation(lang: string): Observable<any> {
    const translationTemplate = this.getTranslationTemplate();
    // If translation template exists and is not 'default', try to load template-specific file first
    if (translationTemplate && translationTemplate !== 'default') {
      const templateSpecificPath = `${this.prefix}${translationTemplate}/${lang}${this.suffix}`;

      return this.http.get(templateSpecificPath).pipe(
        catchError(() => {
          // If template-specific file doesn't exist, fall back to default
          const defaultPath = `${this.prefix}${lang}${this.suffix}`;
          return this.http.get(defaultPath).pipe(
            catchError(() => {
              // If default file also doesn't exist, return empty object
              console.warn(`Translation file not found: ${templateSpecificPath} or ${defaultPath}`);
              return of({});
            })
          );
        })
      );
    }

    // No template or template is 'default', load default file
    const defaultPath = `${this.prefix}${lang}${this.suffix}`;
    return this.http.get(defaultPath).pipe(
      catchError(() => {
        console.warn(`Translation file not found: ${defaultPath}`);
        return of({});
      })
    );
  }
}
